import { useEffect, useRef } from 'react';

/** Teclas como aparecem na tela (Ctrl vale também para o Cmd do Mac). */
export function comboDe(evento: KeyboardEvent): string | null {
  const nomes: Record<string, string> = {
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    Escape: 'Esc',
    Enter: 'Enter',
    Home: 'Home',
    End: 'End',
    ' ': 'Espaço',
  };
  if (['Control', 'Shift', 'Alt', 'Meta'].includes(evento.key)) return null;
  let tecla = nomes[evento.key];
  if (!tecla) {
    // Com Shift/Alt o `key` muda (Alt+2 vira ™ no Mac): usa a tecla física.
    if (/^Key[A-Z]$/.test(evento.code)) tecla = evento.code.slice(3);
    else if (/^Digit\d$/.test(evento.code)) tecla = evento.code.slice(5);
    else if (evento.code === 'Slash') tecla = '/';
    else
      tecla = evento.key.length === 1 ? evento.key.toUpperCase() : evento.key;
  }
  const partes: string[] = [];
  if (evento.ctrlKey || evento.metaKey) partes.push('Ctrl');
  if (evento.altKey) partes.push('Alt');
  if (evento.shiftKey) partes.push('Shift');
  partes.push(tecla);
  return partes.join('+');
}

/** Os atalhos de sempre (C33), por grupo; os do compositor ficam nele. */
export const ATALHOS_PADRAO: Array<{
  grupo: string;
  itens: Array<{ descricao: string; teclas: string[] }>;
}> = [
  {
    grupo: 'Navegação',
    itens: [
      { descricao: 'Troca rápida', teclas: ['Ctrl+K'] },
      { descricao: 'Canal anterior/próximo', teclas: ['Alt+↑', 'Alt+↓'] },
      { descricao: 'Próximo não lido', teclas: ['Alt+Shift+↓'] },
      { descricao: 'Voltar ao canal anterior', teclas: ['Ctrl+Alt+←'] },
      { descricao: 'Caixa de entrada', teclas: ['Ctrl+I'] },
    ],
  },
  {
    grupo: 'Leitura',
    itens: [
      { descricao: 'Marcar canal como lido', teclas: ['Esc'] },
      { descricao: 'Marcar servidor como lido', teclas: ['Shift+Esc'] },
      { descricao: 'Pular para o início', teclas: ['Ctrl+Home'] },
    ],
  },
  {
    grupo: 'Mensagens',
    itens: [
      { descricao: 'Enviar', teclas: ['Enter'] },
      { descricao: 'Nova linha', teclas: ['Shift+Enter'] },
      { descricao: 'Editar última mensagem', teclas: ['↑'] },
      { descricao: 'Cancelar resposta', teclas: ['Esc'] },
      { descricao: 'Enviar arquivo', teclas: ['Ctrl+Shift+U'] },
      { descricao: 'Emojis', teclas: ['Ctrl+E'] },
    ],
  },
  {
    grupo: 'Ações',
    itens: [
      { descricao: 'Buscar', teclas: ['Ctrl+F'] },
      { descricao: 'Fixadas', teclas: ['Ctrl+P'] },
      { descricao: 'Membros', teclas: ['Ctrl+U'] },
      { descricao: 'Esta lista', teclas: ['Ctrl+/'] },
    ],
  },
];

/**
 * Pedido entre partes da tela que não se conhecem (o atalho global pede, o
 * cabeçalho ou o compositor faz): um evento `chat:<nome>` na janela.
 */
export type PedidoTela =
  | 'caixa'
  | 'buscar'
  | 'fixadas'
  | 'membros'
  | 'enviar-arquivo'
  | 'emojis'
  | 'ir-inicio';

export function pedir(pedido: PedidoTela): void {
  window.dispatchEvent(new CustomEvent(`chat:${pedido}`));
}

/** Atende um pedido enquanto o componente está na tela. */
export function useAtender(
  pedido: PedidoTela,
  fazer: () => void,
  ativo = true,
) {
  const ref = useRef(fazer);
  useEffect(() => {
    ref.current = fazer;
  });
  useEffect(() => {
    if (!ativo) return;
    const ouvir = () => ref.current();
    window.addEventListener(`chat:${pedido}`, ouvir);
    return () => window.removeEventListener(`chat:${pedido}`, ouvir);
  }, [pedido, ativo]);
}
